<template>
  <Modal
    :show="true"
    size="sm"
    headerVariant="blue"
    :closeOnBackdrop="false"
    :blurBackdrop="false"
    :backdropOpacity="0.5"
    :show-close-button="false"
    @close="closeModal"
  >
    <template #header>
      <div class="bucket-header__text">
        <h2 class="bucket-header__title">Edit Bucket</h2>
        <p class="bucket-header__subtitle">Update bucket details</p>
      </div>
      <button type="button" class="bucket-header__close" @click="closeModal" aria-label="Close">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </template>
    <template #body>
      <Form id="edit-bucket-form" class="form-body" @submit="editBucketOption">
        <div class="form-group">
          <Input
            v-model="editBucketOptionObj.name"
            label="Bucket Name"
            placeholder="Enter Bucket Name"
            required
            size="md"
          />
        </div>
        <div class="color-options">
          <div
            v-for="(color, index) in bucketColorOptions"
            :key="index"
            class="color-circle"
            :style="{
              backgroundColor: color,
              boxShadow: editBucketOptionObj.colorHex === color ? '0 0 0 6px #E0E0E0' : 'none',
            }"
            @click="editBucketOptionObj.colorHex = color"
          ></div>
        </div>
      </Form>
    </template>
    <template #footer>
      <Button variant="secondary" size="sm" @click="closeModal">Cancel</Button>
      <Button
        variant="primary"
        size="sm"
        type="submit"
        form="edit-bucket-form"
        :loading="bucketOptionIsLoading"
        :disabled="bucketOptionIsLoading"
      >
        Save Changes
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { Form } from "vee-validate";
import { Button, Icon, Input, Modal } from "@/components/ui";
import { useUpdateBucketOption } from "@/composables/bucket/useBucketOption";
import { useToast } from "@/composables/ui/useToast";

interface Props {
  selectedBucketOption: { id: string; name: string; colorHex: string } | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  closeEditBucketOptionModal: [];
  refreshBucketOptions: [];
}>();

const $toast = useToast();

const { mutate: updateBucketOption, isPending: bucketOptionIsLoading } = useUpdateBucketOption();

const editBucketOptionObj = ref<{
  id: string | undefined;
  name: string | undefined;
  colorHex: string | undefined;
}>({
  id: undefined,
  name: undefined,
  colorHex: undefined,
});

const bucketColorOptions = [
  "#FA5A7D",
  "#FFD900",
  "#3CD856",
  "#3C3CFA",
  "#00A896",
  "#002B72",
  "#0005FF",
  "#7B003D",
  "#FF002C",
  "#DC00FD",
  "#2D0094",
  "#00AEFD",
  "#00FFAB",
  "#65A700",
];

function setValues(option: Props["selectedBucketOption"]) {
  if (!option) return;
  editBucketOptionObj.value.id = option.id;
  editBucketOptionObj.value.name = option.name;
  editBucketOptionObj.value.colorHex = option.colorHex;
}

function closeModal() {
  emit("closeEditBucketOptionModal");
}

function editBucketOption() {
  if (!editBucketOptionObj.value.colorHex) {
    $toast?.info("Please select a color");
    return;
  }

  updateBucketOption(
    {
      id: editBucketOptionObj.value.id!,
      name: editBucketOptionObj.value.name!,
      colorHex: editBucketOptionObj.value.colorHex,
    },
    {
      onSuccess: () => {
        emit("refreshBucketOptions");
        closeModal();
      },
      onError: (error: Error) => {
        $toast?.error(error.message || "Failed to update bucket");
      },
    }
  );
}

watch(
  () => props.selectedBucketOption,
  (v) => setValues(v),
  { immediate: true }
);
</script>

<style scoped>
.bucket-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bucket-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.01em;
}

.bucket-header__subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}

.bucket-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.bucket-header__close:hover {
  background: rgba(255, 255, 255, 0.35);
}

.form-body {
  padding: 16px 20px;
}

.form-group {
  margin-bottom: 16px;
}

.color-options {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 15px;
  padding-left: 12px;
  padding-right: 12px;
}

.color-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.color-circle:hover {
  transform: scale(1.1);
}
</style>
